// components/ErrorBoundary.js
import React from 'react';
import { trackEvent, trackException } from '../services/api/TrackingService'; // TrackingServiceをインポート

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // エラーID生成
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    
    this.setState({
      error,
      errorInfo,
      errorId
    });

    // TrackingServiceを使用してエラーログをバックエンドAPIに送信
    try {
      trackException(error, {
        errorId,
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        buildVersion: process.env.REACT_APP_VERSION || 'unknown',
        source: 'ErrorBoundary'
      });
    } catch (loggingError) {
      console.error('エラーログの送信に失敗:', loggingError);
    }

    // 開発環境では詳細ログ
    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Error Boundary Caught Error');
      console.error('Error ID:', errorId);
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Component Stack:', errorInfo.componentStack);
      console.groupEnd();
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorPage 
          error={this.state.error}
          errorId={this.state.errorId}
          onReload={this.handleReload}
          onGoHome={this.handleGoHome}
          showDetails={process.env.NODE_ENV === 'development'}
        />
      );
    }

    return this.props.children;
  }
}

// ErrorPage コンポーネント
const ErrorPage = ({ error, errorId, onReload, onGoHome, showDetails = false }) => {
  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.iconContainer}>
          <svg width="80" height="80" viewBox="0 0 24 24" fill="#ef4444">
            <path d="M12 2L1 21h22L12 2zm0 3.5L19.5 19h-15L12 5.5zM11 16v2h2v-2h-2zm0-6v4h2V10h-2z"/>
          </svg>
        </div>
        
        <h1 style={styles.title}>申し訳ございません</h1>
        <p style={styles.message}>
          予期しないエラーが発生しました。<br />
          問題が解決しない場合は、管理者にお問い合わせください。
        </p>
        
        {errorId && (
          <div style={styles.errorId}>
            <strong>エラーID:</strong> {errorId}
          </div>
        )}
        
        <div style={styles.actions}>
          <button style={styles.primaryButton} onClick={onReload}>
            ページを再読み込み
          </button>
          <button style={styles.secondaryButton} onClick={onGoHome}>
            ホームに戻る
          </button>
        </div>
        
        {showDetails && error && (
          <details style={styles.details}>
            <summary style={styles.summary}>技術的な詳細（開発モード）</summary>
            <div style={styles.errorDetails}>
              <h4>エラーメッセージ:</h4>
              <pre style={styles.errorText}>{error.message}</pre>
              
              <h4>スタックトレース:</h4>
              <pre style={styles.errorText}>{error.stack}</pre>
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

// NotFoundPage - 404エラー用
const NotFoundPage = () => {
  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleGoBack = () => {
    window.history.back();
  };

  // 404ページビューを記録
  React.useEffect(() => {
    // trackEventを使用して404ページビューを記録
    trackEvent({
      name: 'PageNotFound',
      properties: {
        url: window.location.href,
        referrer: document.referrer,
        timestamp: new Date().toISOString()
      }
    }).catch(error => console.error('404ページビューログ送信エラー:', error));
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.iconContainer}>
          <div style={styles.notFoundNumber}>404</div>
        </div>
        
        <h1 style={styles.title}>ページが見つかりません</h1>
        <p style={styles.message}>
          お探しのページは存在しないか、移動された可能性があります。<br />
          URLをご確認いただくか、ホームページからお探しください。
        </p>
        
        <div style={styles.actions}>
          <button style={styles.primaryButton} onClick={handleGoHome}>
            ホームに戻る
          </button>
          <button style={styles.secondaryButton} onClick={handleGoBack}>
            前のページに戻る
          </button>
        </div>
      </div>
    </div>
  );
};

// カスタムフック - エラーハンドリング用
const useErrorHandler = () => {
  const handleError = React.useCallback((error, context = {}) => {
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    
    try {
      trackException(error, {
        errorId,
        context: JSON.stringify(context),
        url: window.location.href,
        timestamp: new Date().toISOString(),
        source: 'useErrorHandler'
      });
    } catch (loggingError) {
      console.error('エラーログの送信に失敗:', loggingError);
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.error('Error handled:', error, context);
    }
    
    return errorId;
  }, []);

  return { handleError };
};

// スタイル定義
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8fafc',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  content: {
    textAlign: 'center',
    maxWidth: '500px',
    width: '100%',
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  },
  iconContainer: {
    marginBottom: '24px'
  },
  notFoundNumber: {
    fontSize: '72px',
    fontWeight: 'bold',
    color: '#6b7280',
    lineHeight: 1
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '16px',
    margin: 0
  },
  message: {
    fontSize: '16px',
    color: '#6b7280',
    lineHeight: 1.6,
    marginBottom: '32px'
  },
  errorId: {
    fontSize: '14px',
    color: '#9ca3af',
    backgroundColor: '#f3f4f6',
    padding: '8px 12px',
    borderRadius: '6px',
    marginBottom: '24px',
    fontFamily: 'monospace'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  primaryButton: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  details: {
    marginTop: '32px',
    textAlign: 'left',
    borderTop: '1px solid #e5e7eb',
    paddingTop: '20px'
  },
  summary: {
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '12px'
  },
  errorDetails: {
    marginTop: '12px'
  },
  errorText: {
    backgroundColor: '#f3f4f6',
    padding: '12px',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#374151',
    overflow: 'auto',
    maxHeight: '200px',
    marginBottom: '12px'
  }
};

export { ErrorBoundary, ErrorPage, NotFoundPage, useErrorHandler };